
import { pipe, split, mergeWith, flip, equals, lensProp, lensPath, view, is, curry, set as ramdaSet } from 'ramda';
import { isImmutable, is as immutableIs, Set } from 'immutable';

/**
 *
 * @param path
 * @returns {*}
 */

const getPath = path => (is(Array, path) ? lensPath(path) : lensProp(path));

export const get = curry((prop, obj) => view(getPath(prop), obj));

export const set = curry((prop, value, obj) => ramdaSet(getPath(prop), value, obj));

export const checkSameProps = (props, nextProps, propKeysToValidate) =>
  propKeysToValidate.some(key => {
    const nextProp = nextProps[key];
    const prop = props[key];
    if (isImmutable(prop)) {
      return !immutableIs(prop, nextProp);
    } else {
      return !equals(prop, nextProp);
    }
  });

export const defaults = mergeWith((left, right) => left === undefined ? right : left);


/**
 * Formateja un import en format de 2 decimals, el símbol de la divisa rebuda (o la actual) i els separadors de l'idioma actual
 * Si no es passa el simbol de la divisa s'intentarà utilitzar la divisa seleccionada actualment o la divisa per defecte
 * @param currencies
 * @param currentCurrencyCode
 * @param langInfo
 * @param symbolBefore
 * @returns {function(*=, *=, *=): string}
 */
export const formatCurrencyAmount = (currencies, currentCurrencyCode, currencyInfo) => (amount, symbol, decPlaces = 2, symbolBefore) => {
  let c; // currency
  if (symbol === undefined) {
    c = currencies.find(c => (c.code = currentCurrencyCode)) || currencies.find(c => c.ratio === 1);
  }
  const decSeparator = currencyInfo.get('decimal_separator', ',');
  const thouSeparator = currencyInfo.get('thousand_separator', '.');
  symbol = symbol === undefined ? (c ? c.symbol || c.code : '') : symbol;
  if (symbolBefore) {
    if(amount < 0) {
      return '-' + symbol + '' + formatNumber(Math.abs(amount), decPlaces, thouSeparator, decSeparator);
    }
    return symbol + '' + formatNumber(amount, decPlaces, thouSeparator, decSeparator);
  }
  return formatNumber(amount, decPlaces, thouSeparator, decSeparator) + (symbol ? ' ' + symbol : '');
};

/**
 * Convert amount to specific ratio
 * @param amount
 * @param fromRatio
 * @param toRatio
 * @returns {number}
 */
export const getPrice = (amount, fromRatio, toRatio) => {
  return amount * ((1 / parseFloat(fromRatio)) * parseFloat(toRatio));
};

/**
 *
 * @param number
 * @param decPlaces
 * @param thouSeparator
 * @param decSeparator
 * @returns {string}
 */
export const formatNumber = (number, decPlaces, thouSeparator, decSeparator) => {
  decPlaces = isNaN((decPlaces = Math.abs(decPlaces))) ? 2 : decPlaces;
  thouSeparator = thouSeparator === undefined ? ',' : thouSeparator;
  decSeparator = decSeparator === undefined ? '.' : decSeparator;
  const sign = number < 0 ? '-' : '';
  const i =
    parseInt((number = Number(Math.round(Math.abs(+number || 0) + 'e' + decPlaces) + 'e-' + decPlaces)), 10) + '';
  const j = i.length > 3 ? i.length % 3 : 0;
  return (
    sign +
    (j ? i.substr(0, j) + thouSeparator : '') +
    i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thouSeparator) +
    (decPlaces
      ? decSeparator +
      Number(Math.round(Math.abs(number - i) + 'e' + decPlaces) + 'e-' + decPlaces)
        .toFixed(decPlaces)
        .slice(2)
      : '')
  );
};

/**
 *
 * @param number
 * @param langInfo
 * @returns {string}
 */
export const formatToSi = (number,currencyInfo) => {
  const decimalSeparator = currencyInfo.get('decimal_separator', ",");
  let formattedNumber = '';
  let suffix = '';

  if (number >= 1000000) {
    formattedNumber = (number / 1000000);
    suffix = 'M';
  } else if (number >= 1000) {
    formattedNumber = (number / 1000);
    suffix = 'K';
  } else {
    formattedNumber = Math.round(number);
  }
  formattedNumber = Math.round(formattedNumber * 10) / 10 + suffix;
  return formattedNumber.replace('.', decimalSeparator);
};

export function hashOnly(url) {
  return url && url.replace(/.*?(#|%23)/, '');
}

export function removeHash(url) {
  return url && url.replace(/(#|%23).*/, '');
}

export const hashSpliter = pipe(
  hashOnly,
  split('/')
  //(value) => { return !is(Array, value) ? value : [value] }
);

/**
 *
 * @param keys
 * @returns {function(*, *=): boolean}
 */
export const keyIn = (...keys) => (v, k) => Set(keys).has(k);
